import { Navigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useDataManagement } from './RequisitarAPI.ts';

/**
 * Componente de rota protegida que verifica autenticação e nível de acesso
 * @param {Object} props - Propriedades do componente
 * @param {React.ReactNode} props.children - Componente filho a ser renderizado se autenticado
 * @param {string[]} [props.requiredAccessLevels] - Níveis de acesso requeridos para acessar a rota
 * @returns {React.ReactNode} - O componente filho ou redirecionamento para login
 */
const PrivateRoute = ({ children, requiredAccessLevels = [] }) => {
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const { users } = useDataManagement();
  const location = useLocation();

  // Efeito para verificar autenticação e permissões
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Verifica se o usuário está autenticado e obtém seu nível de acesso
        const result = await users.getCurrentUser();

        setIsAuthenticated(result.ok);

        // Se autenticado e há requisitos de nível de acesso, verifica permissões
        if (result.ok && requiredAccessLevels.length > 0) {
          const userAccessLevel = result.data?.access_level || '';
          setHasAccess(requiredAccessLevels.includes(userAccessLevel));
        } else if (result.ok) {
          // Se não há requisitos específicos de nível, apenas estar autenticado é suficiente
          setHasAccess(true);
        }
      } catch (error) {
        console.error('Erro ao verificar autenticação:', error);
        setIsAuthenticated(false);
        setHasAccess(false);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [users, requiredAccessLevels]);

  // Enquanto verifica autenticação, pode mostrar uma tela de carregamento
  if (loading) {
    return <div id="loading-overlay">
      <div>
        <div id="loading-spinner"></div>
        <p>Verificando acesso...</p>
      </div>
    </div>
  }

  // Se não estiver autenticado, redireciona para login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname, message: "Por favor, faça login para acessar esta página." }} replace />;
  }

  // Se estiver autenticado mas não tiver as permissões necessárias
  if (!hasAccess) {
    return <Navigate to="/" state={{ message: "Você não tem permissão para acessar esta página." }} replace />;
  }

  // Se estiver autenticado e tiver as permissões necessárias
  return children;
};

export default PrivateRoute; 